import { useEffect, useState } from 'react';

const PREFIX = 'diyt-card-history-v1:';
const merge = (a, b) => Object.fromEntries([...new Set([...Object.keys(a), ...Object.keys(b)])].map(id => [id, Math.max(a[id] || 0, b[id] || 0)]));
const empty = () => ({ epoch: null, generation: 0, played: {}, pending: {}, imported: false, clockOffset: 0 });
const read = (storage, key, fallback) => { try { return JSON.parse(storage.getItem(key)) ?? fallback; } catch { return fallback; } };
function bounded(promise) {
  let timer;
  return Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(Error('Account connection timed out.')), 8000); })]).finally(() => clearTimeout(timer));
}

// Local state is an offline cache. The signed-in account is authoritative at sync.
export function createCardHistory({ storage = localStorage, account = () => window.RecessAccount?.client, request = fetch } = {}) {
  let scope = 'guest', user = null, generation = 0, disposed = false, timer, syncing;
  let state = read(storage, PREFIX + scope, empty());
  if (!state.imported) {
    const legacy = read(storage, 'diyt-played-v2', []);
    if (Array.isArray(legacy)) state.played = merge(state.played, Object.fromEntries(legacy.map((id, i) => [id, i + 1])));
    state.imported = true;
  }
  let ready = !account(), busy = false, status = account() ? 'Checking your card history...' : 'Saved on this device.';
  const listeners = new Set();
  const persist = () => { try { storage.setItem(PREFIX + scope, JSON.stringify(state)); } catch { status = 'Device storage is unavailable. Keep this game open to retain your history.'; } };
  const snapshot = () => ({ played: state.played, scope, epoch: state.epoch, ready, busy, status });
  const emit = () => { persist(); if (!disposed) listeners.forEach(fn => fn(snapshot())); };
  const switchUser = next => {
    const nextScope = next ? `user:${next.id}` : 'guest';
    try { storage.setItem(PREFIX + 'last-user', JSON.stringify(next?.id || null)); } catch {}
    user = next;
    if (nextScope !== scope) {
      persist();
      generation++;
      scope = nextScope;
      state = read(storage, PREFIX + scope, empty());
      clearTimeout(timer);
    }
  };
  async function api(body) {
    const client = account();
    const token = await bounded(client?.token());
    if (!token) throw Error('Sign in again to sync your cards. Your history is safe on this device.');
    const response = await bounded(request('https://power.drinkifyouthink.com/api/library/card-history/drink-if-you-think', {
      method: body ? 'POST' : 'GET', cache: 'no-store',
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    }));
    const data = await response.json();
    if (!response.ok) { const error = Error(data.error || 'Your cards are saved here. Account sync will retry.'); error.history = data.history; throw error; }
    return data.history;
  }
  function adopt(remote) {
    state = { ...state, epoch: remote.epoch, generation: remote.generation, played: remote.played, pending: {}, clockOffset: remote.serverTime ? remote.serverTime - Date.now() : state.clockOffset || 0 };
  }
  async function synchronize(checkAccount = true) {
    // Serialize account requests so a slower response cannot overwrite a newer one.
    while (syncing) { await syncing; if (disposed) return snapshot(); }
    const run = async () => {
      const started = generation;
      let current = generation;
      try {
        if (checkAccount && account()) {
          const next = await bounded(account().session());
          if (generation !== started || disposed) return;
          switchUser(next);
        }
        if (!user) { ready = true; status = 'Saved on this device.'; emit(); return; }
        current = generation;
        const remote = await api();
        if (current !== generation || disposed) return;
        if (state.epoch && state.epoch !== remote.epoch) {
          adopt(remote); // A reset on another device must discard old queued plays.
        } else {
          let pending = state.pending;
          if (!state.imported) {
            const guest = read(storage, PREFIX + 'guest', empty());
            // Never resurrect browser history after an account-wide reset.
            if (remote.generation === 0 && !guest.claimed) pending = merge(pending, guest.played);
            state.imported = true;
            try { storage.setItem(PREFIX + 'guest', JSON.stringify({ ...guest, claimed: true })); } catch {}
          }
          state = { ...state, epoch: remote.epoch, generation: remote.generation, played: merge(remote.played, pending), pending, clockOffset: remote.serverTime ? remote.serverTime - Date.now() : state.clockOffset || 0 };
        }
        ready = true;
        emit();
        if (Object.keys(state.pending).length) {
          const pending = { ...state.pending }, epoch = state.epoch;
          const saved = await api({ epoch, played: pending });
          if (current !== generation || disposed) return;
          const remaining = Object.fromEntries(Object.entries(state.pending).filter(([id, rank]) => rank !== pending[id]));
          state = { ...state, epoch: saved.epoch, played: merge(saved.played, remaining), pending: remaining };
        }
        status = Object.keys(state.pending).length ? 'Saving your card history...' : 'Saved to your account.';
        ready = true; emit();
      } catch (error) {
        if (disposed || current !== generation) return;
        if (!user && scope === 'guest') {
          const previous = read(storage, PREFIX + 'last-user', null);
          if (typeof previous === 'string' && previous) {
            persist(); scope = `user:${previous}`; state = read(storage, PREFIX + scope, empty());
            generation++;
          }
        }
        if (error.history) { adopt(error.history); status = 'History updated from your other device.'; }
        else status = 'Saved on this device. Account sync will retry when connected.';
        ready = true; emit();
      }
    };
    const operation = run(); syncing = operation;
    try { await operation; } finally { if (syncing === operation) syncing = null; }
    return snapshot();
  }
  function record(id) {
    const rank = Math.max(Date.now() + (state.clockOffset || 0), ...Object.values(state.played)) + 1;
    state = { ...state, played: { ...state.played, [id]: rank }, pending: scope !== 'guest' ? { ...state.pending, [id]: rank } : {} };
    status = user ? 'Saving your card history...' : 'Saved on this device.';
    emit();
    clearTimeout(timer);
    if (user) timer = setTimeout(() => synchronize(false), 200);
  }
  async function reset() {
    if (busy) return false;
    const current = generation;
    busy = true; emit(); clearTimeout(timer);
    if (syncing) await syncing;
    if (current !== generation || disposed) { busy = false; emit(); return false; }
    try {
      if (scope !== 'guest') {
        if (!user) throw Error('Reconnect before resetting account history.');
        // A failed/reset-raced request never silently clears a different epoch.
        const remote = await api({ epoch: state.epoch, reset: true });
        if (current !== generation || disposed) return false;
        adopt(remote);
      } else state = { ...empty(), epoch: `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`, imported: true, claimed: state.claimed };
      status = user ? 'Card history reset on your account.' : 'Card history reset on this device.';
      return true;
    } catch (error) {
      if (current !== generation || disposed) return false;
      if (error.history) adopt(error.history);
      status = error.history ? 'History changed on another device. Review it before resetting.' : 'Could not reset your account history. Try again when connected.';
      return false;
    } finally { busy = false; emit(); }
  }
  return {
    snapshot, record, reset,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    async prepare() { busy = true; emit(); try { return await synchronize(); } finally { busy = false; emit(); } },
    start() { disposed = false; return synchronize(); },
    accountChanged(next) { switchUser(next); ready = !next; emit(); return synchronize(false); },
    dispose() { disposed = true; clearTimeout(timer); listeners.clear(); }
  };
}

export function useCardHistory() {
  const [store] = useState(() => createCardHistory({ request: (...args) => fetch(...args) }));
  const [history, setHistory] = useState(store.snapshot);
  useEffect(() => {
    const off = store.subscribe(setHistory);
    const refresh = () => store.start();
    const changed = event => store.accountChanged(event.detail.user);
    store.start();
    document.addEventListener('recess-ready', refresh);
    document.addEventListener('recess-account-change', changed);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    return () => {
      off(); store.dispose();
      document.removeEventListener('recess-ready', refresh);
      document.removeEventListener('recess-account-change', changed);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [store]);
  return { ...history, record: store.record, reset: store.reset, prepare: store.prepare };
}
